import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { comics, categories, statusLabels } from '@/mocks/comics';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';

type SortOption = 'newest' | 'popular' | 'rating';
type StatusFilter = 'all' | 'serializing' | 'completed';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();

  const initialStatus = (searchParams.get('status') as StatusFilter) || 'all';
  const initialSort = (searchParams.get('sort') as SortOption) || 'newest';
  const initialSearch = searchParams.get('search') || '';

  const [statusFilter, setStatusFilter] = useState<StatusFilter>(initialStatus);
  const [sortBy, setSortBy] = useState<SortOption>(initialSort);
  const [searchText, setSearchText] = useState(initialSearch);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  const categoryInfo = categories.find(c => c.slug === slug);
  const isAll = slug === 'all';

  useEffect(() => {
    setCurrentPage(1);
  }, [slug, statusFilter, sortBy, searchText]);

  const filteredComics = useMemo(() => {
    let result = [...comics];

    if (!isAll && slug) {
      result = result.filter(c => c.categorySlug === slug);
    }

    if (statusFilter !== 'all') {
      result = result.filter(c => c.status === statusFilter);
    }

    if (searchText) {
      const query = searchText.toLowerCase();
      result = result.filter(
        c =>
          c.title.includes(searchText) ||
          c.author.includes(searchText) ||
          c.tags?.some(t => t.toLowerCase().includes(query))
      );
    }

    switch (sortBy) {
      case 'newest':
        result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
        break;
      case 'popular':
        result.sort((a, b) => b.views - a.views);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
    }

    return result;
  }, [slug, isAll, statusFilter, sortBy, searchText]);

  const totalPages = Math.ceil(filteredComics.length / pageSize);
  const pageComics = filteredComics.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const pageTitle = isAll
    ? '全部分类'
    : categoryInfo
      ? `${categoryInfo.name}漫画`
      : '分类';

  useEffect(() => {
    document.title = `${pageTitle} - 韩国漫画大全 | 免费在线韩漫阅读网`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', `${pageTitle}，韩国漫画大全免费在线韩漫阅读平台，收录超万部韩漫作品。浏览${pageTitle}分类下的热门韩漫、经典韩漫，全部免费阅读。`);
    }
  }, [pageTitle]);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    ...(isAll ? [] : [{ label: '全部分类', href: '/category/all' }]),
    { label: pageTitle },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.href ? `http://w340q.cn/${item.href}` : undefined,
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <Breadcrumb items={breadcrumbItems} />

      <section className="px-4 md:px-6 py-6 md:py-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950">
              {pageTitle}
            </h1>
            {categoryInfo && (
              <p className="mt-2 text-sm text-foreground-500">
                共收录 {categoryInfo.count.toLocaleString()} 部{categoryInfo.name}类韩漫作品
              </p>
            )}
            {isAll && (
              <p className="mt-2 text-sm text-foreground-500">
                共收录 {comics.length.toLocaleString()} 部韩漫作品，涵盖全部类型
              </p>
            )}
          </div>

          {isAll && (
            <div className="flex items-center flex-wrap gap-2 mb-8">
              <Link
                to="/category/all"
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  slug === 'all' && !searchParams.get('category')
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-200/80 text-foreground-600 hover:bg-background-300'
                }`}
              >
                全部
              </Link>
              {categories.map(cat => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="px-3 py-1.5 rounded-full text-sm font-medium bg-background-200/80 text-foreground-600 hover:bg-background-300 transition-colors whitespace-nowrap"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center rounded-full bg-background-200/60 p-1">
                {(['all', 'serializing', 'completed'] as StatusFilter[]).map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                      statusFilter === s
                        ? 'bg-background-50 text-foreground-950'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {s === 'all' ? '全部状态' : statusLabels[s]}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-accent-400 cursor-pointer"
              >
                <option value="newest">最新更新</option>
                <option value="popular">最受欢迎</option>
                <option value="rating">评分最高</option>
              </select>
            </div>
          </div>

          {filteredComics.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                <i className="ri-file-search-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">没有找到相关漫画</p>
              <p className="text-sm text-foreground-400 mt-1">试试其他筛选条件</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {pageComics.map(comic => (
                  <ComicCard key={comic.id} comic={comic} variant="default" />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-12">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-300 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).slice(
                    Math.max(0, Math.min(currentPage - 3, totalPages - 5)),
                    Math.min(totalPages, Math.max(currentPage + 2, 5))
                  ).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                        currentPage === page
                          ? 'bg-accent-500 text-background-50'
                          : 'border border-background-300 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-300 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}

              <div className="text-center mt-8">
                <p className="text-sm text-foreground-400">
                  共 {filteredComics.length} 部作品 · 第 {currentPage}/{totalPages} 页
                </p>
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}